import React, { FC, createContext, useContext } from 'react';
import { Typography, IconButton, Avatar, Box, Stack, TextField, Divider, Button, Alert } from '@mui/material';
import { Cancel, CheckCircle, Delete, Edit, LockOpen, PushPin, PushPinOutlined, Reply, Save, Send } from '@mui/icons-material';
import moment from 'moment';
import styled from '@emotion/styled';
import { useAuth } from 'oidc-react';
import { GuideCommentStatus } from '../__generated__/graphql';
import { theme } from '../theme';
import { formatDateOnly } from '../Formatters';
import { RichText, RichTextField, htmlIsEmpty } from '../components/richText';

/** Set by surfaces whose comments are sanitized rich text (the dictionary); the rest stay plain text. */
export const RichTextComments = createContext(false);

/**
 * The comment thread UI shared by guide comments and category comments: one item with its
 * replies, and the selectable list of them. The GraphQL fragments for GuideCommentModel and
 * CategoryCommentModel both satisfy `Thread`; what differs is the set of mutations behind it,
 * which the caller supplies as `ThreadActions` (see useGuideCommentActions / useCategoryCommentActions).
 * Without actions the item renders read-only, which is what the notifications page needs.
 */

export interface ThreadUser {
  subjectId: string;
  name?: string | null;
}

export interface ThreadReply {
  id: string;
  comment?: string | null;
  createdBy?: ThreadUser | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface Thread {
  id: string;
  text?: string | null;
  status: GuideCommentStatus;
  isPinned?: boolean | null;
  externalUserEmail?: string | null;
  createdBy?: ThreadUser | null;
  createdAt: string;
  updatedAt?: string | null;
  closedAt?: string | null;
  closedBy?: ThreadUser | null;
  replies?: (ThreadReply | null)[] | null;
}

export interface ThreadActions {
  remove: (commentId: string) => Promise<unknown>;
  setStatus: (commentId: string, status: GuideCommentStatus) => Promise<unknown>;
  /** Not every surface offers pinning (the overview does not). */
  pin?: (commentId: string, isPinned: boolean) => Promise<unknown>;
  update: (commentId: string, text: string) => Promise<unknown>;
  createReply: (commentId: string, comment: string) => Promise<unknown>;
  updateReply: (commentId: string, replyId: string, comment: string) => Promise<unknown>;
  deleteReply: (commentId: string, replyId: string) => Promise<unknown>;
}

export function sortThreads<T extends Thread>(a: T, b: T): number {
  if (a.isPinned && !b.isPinned) return -1;
  if (!a.isPinned && b.isPinned) return 1;
  if (moment(b.createdAt).isAfter(a.createdAt)) return 1;
  if (moment(a.createdAt).isAfter(b.createdAt)) return -1;
  return 0;
}

export const CommentThreadList: FC<{ comments: Thread[]; actions: ThreadActions }> = ({ comments, actions }) => {
  const [selectedCommentId, setSelectedCommentId] = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setSelectedCommentId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Stack ref={containerRef} spacing={2} divider={<Divider />}>
      {[...comments].sort(sortThreads).map((c) => (
        <CommentItem
          key={c.id}
          comment={c}
          actions={actions}
          selected={c.id === selectedCommentId}
          onCommentSelected={() => setSelectedCommentId(c.id)}
        />
      ))}
    </Stack>
  );
};

export const CommentItem: FC<{ comment: Thread; selected: boolean; onCommentSelected?: () => void; actions?: ThreadActions }> = ({
  comment,
  selected,
  onCommentSelected,
  actions,
}) => {
  const { createdBy, text } = comment;
  const author = createdBy ? createdBy.name : 'Ukjent';
  const isSelected = selected;
  const auth = useAuth();
  const profile = auth.userData?.profile;
  const isEditable = !!actions && comment.createdBy?.subjectId === profile?.sub;
  const isClosed = comment.status === GuideCommentStatus.Closed;

  const richText = useContext(RichTextComments);
  const [replyContent, setReplyContent] = React.useState('');
  const [sendingReply, setSendingReply] = React.useState(false);
  const replyIsEmpty = richText ? htmlIsEmpty(replyContent) : !replyContent.trim();

  const createReply = async () => {
    if (!actions || sendingReply || replyIsEmpty) return;
    setSendingReply(true);
    try {
      await actions.createReply(comment.id, replyContent);
      setReplyContent('');
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <StyledComment id={`comment-${comment.id}`} onClick={onCommentSelected}>
      <Stack spacing={1}>
        {isClosed && (
          <Alert
            action={
              actions ? (
                <Button
                  size="small"
                  color="success"
                  variant="contained"
                  startIcon={<LockOpen fontSize="small" />}
                  onClick={() => actions.setStatus(comment.id, GuideCommentStatus.Open)}>
                  Gjenåpne
                </Button>
              ) : undefined
            }>
            Lukket av {comment.closedBy?.name} for {moment(comment.closedAt).startOf('seconds').fromNow()}
          </Alert>
        )}
        <CommentContent
          author={comment.externalUserEmail ?? author!}
          useInitials={!comment.externalUserEmail}
          comment={text ?? ''}
          createdAt={comment.createdAt}
          updatedAt={comment.updatedAt}
          isSelected={isSelected}
          onUpdate={isEditable ? (value) => actions!.update(comment.id, value) : undefined}
          actions={
            <StyledThreadHeader>
              {comment.isPinned && !(actions?.pin && isSelected) && <PushPin fontSize="small" color="action" titleAccess="Festet" />}
              {actions && isSelected && (
                <>
                  {actions.pin && (
                    <IconButton size="small" title={comment.isPinned ? 'Løs ut' : 'Fest'} onClick={() => actions.pin!(comment.id, !comment.isPinned)}>
                      {comment.isPinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
                    </IconButton>
                  )}
                  {!isClosed && (
                    <IconButton size="small" color="success" title="Lukk" onClick={() => actions.setStatus(comment.id, GuideCommentStatus.Closed)}>
                      <CheckCircle fontSize="small" />
                    </IconButton>
                  )}
                  {isEditable && (
                    <IconButton size="small" title="Slett" onClick={() => actions.remove(comment.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </>
              )}
            </StyledThreadHeader>
          }
        />
        {(isSelected && actions) || comment.replies?.length ? <Divider sx={{ mt: 2 }} /> : null}
        <StyledRepliesContainer>
          <Stack divider={<Divider />} spacing={2}>
            {comment.replies
              ?.filter((reply): reply is ThreadReply => !!reply)
              .map((reply) => <ReplyItem key={reply.id} isSelected={isSelected} reply={reply} commentId={comment.id} actions={actions} />)}
            {actions && isSelected && !isClosed && (
              <Stack spacing={1}>
                {richText ? (
                  <Box sx={{ width: '100%' }}>
                    <RichTextField value={replyContent} onChange={setReplyContent} placeholder="Skriv svar..." autoFocus />
                  </Box>
                ) : (
                  <TextField
                    size="small"
                    multiline
                    sx={{ width: '100%' }}
                    placeholder="Skriv svar..."
                    autoFocus
                    value={replyContent}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        createReply();
                      }
                    }}
                    onChange={(e) => setReplyContent(e.target.value)}></TextField>
                )}
                <IconButton color="primary" title="Send" sx={{ alignSelf: 'flex-end' }} disabled={replyIsEmpty || sendingReply} onClick={createReply}>
                  <Send />
                </IconButton>
              </Stack>
            )}
          </Stack>
        </StyledRepliesContainer>
        {actions && !isSelected && !isClosed && (
          <Button
            size="small"
            startIcon={<Reply fontSize="small" />}
            sx={{ alignSelf: 'flex-start' }}
            onClick={(e) => {
              e.stopPropagation();
              onCommentSelected?.();
            }}>
            Svar
          </Button>
        )}
      </Stack>
    </StyledComment>
  );
};

const ReplyItem: FC<{ isSelected: boolean; reply: ThreadReply; commentId: string; actions?: ThreadActions }> = ({
  isSelected,
  reply,
  commentId,
  actions,
}) => {
  const { createdBy, comment, createdAt, updatedAt } = reply;
  const author = createdBy ? createdBy.name : 'Ukjent';
  const auth = useAuth();
  const profile = auth.userData?.profile;
  const canUpdate = !!actions && reply.createdBy?.subjectId === profile?.sub;

  return (
    <CommentContent
      author={author!}
      useInitials
      comment={comment ?? ''}
      createdAt={createdAt}
      updatedAt={updatedAt}
      isSelected={isSelected}
      onUpdate={canUpdate ? (value) => actions!.updateReply(commentId, reply.id, value) : undefined}
      onDelete={canUpdate ? () => actions!.deleteReply(commentId, reply.id) : undefined}
    />
  );
};

const CommentContent: FC<{
  createdAt: string;
  updatedAt?: string | null;
  author: string | null;
  useInitials?: boolean;
  comment: string;
  onUpdate?: (text: string) => void;
  onDelete?: () => void;
  isSelected: boolean;
  actions?: React.ReactNode;
}> = ({ onUpdate, isSelected, comment, author, createdAt, updatedAt, onDelete, actions, useInitials }) => {
  const richText = useContext(RichTextComments);
  const [content, setContent] = React.useState(comment);
  const [isEditing, setIsEditing] = React.useState(false);
  return (
    <StyledComment>
      <Box display="grid" gridTemplateColumns="1fr auto" alignItems="top" gap={2} justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <StyledAvatar sx={{ backgroundColor: (theme) => (useInitials ? undefined : theme.palette.primary.light) }}>
            {useInitials ? getInitials(author) : null}
          </StyledAvatar>
          <Stack>
            <Typography variant="subtitle2">{author ?? 'Ekstern bruker'}</Typography>
            <Typography variant="caption">
              {moment(createdAt).isAfter(moment().subtract(1, 'year')) ? moment(createdAt).startOf('seconds').fromNow() : formatDateOnly(createdAt)}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end" alignItems="start">
          {onUpdate && isSelected && !isEditing && (
            <IconButton title="Rediger" size="small" onClick={() => setIsEditing(true)}>
              <Edit fontSize="small" />
            </IconButton>
          )}
          {!isEditing && actions}
          {isEditing && (
            <>
              <IconButton
                title="Lagre"
                size="small"
                onClick={() => {
                  onUpdate?.(content);
                  setIsEditing(false);
                }}>
                <Save fontSize="small" />
              </IconButton>
              <IconButton
                title="Avbryt"
                size="small"
                onClick={() => {
                  setContent(comment);
                  setIsEditing(false);
                }}>
                <Cancel fontSize="small" />
              </IconButton>
            </>
          )}
          {onDelete && isSelected && !isEditing && (
            <IconButton title="Slett" size="small" onClick={onDelete}>
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>
      {!isEditing && (
        <Box sx={{ width: '100%', whiteSpace: richText ? undefined : 'pre-line', '& p': { mt: 0, mb: 1 }, '& p:last-of-type': { mb: 0 } }}>
          {richText ? <RichText html={comment} component="div" /> : comment}
          {updatedAt && (
            <>
              {' '}
              <Typography component="span" color="textSecondary" fontSize="small" title={moment(updatedAt).startOf('seconds').fromNow()}>
                (redigert)
              </Typography>
            </>
          )}
        </Box>
      )}
      {isEditing && richText && <RichTextField value={content} onChange={setContent} autoFocus />}
      {isEditing && !richText && (
        <TextField
          value={content}
          size="small"
          multiline
          sx={{ width: '100%' }}
          onClick={() => setIsEditing(true)}
          onChange={(e) => setContent(e.target.value)}
        />
      )}
    </StyledComment>
  );
};

function getInitials(fullName: string | null | undefined) {
  if (!fullName) return '';
  const names = fullName.split(' ');
  if (names.length === 1) return names[0][0].toUpperCase();
  return (names[0][0] + names[names.length - 1][0]).toUpperCase();
}

/** The first non-empty line of a comment, used as the summary in accordion lists. */
export function firstLine(text: string | null | undefined): string {
  return text?.split('\n').filter((x) => !!x.trim())[0] ?? '';
}

const StyledRepliesContainer = styled.div`
  margin-left: 11px;
  border-left: 3px solid ${theme.palette.divider};
  padding-left: 16px;
`;

const StyledComment = styled.div`
  margin-top: ${theme.spacing(1)};
`;

const StyledAvatar = styled(Avatar)`
  height: 28px;
  width: 28px;
  font-size: 14px;
  margin-top: -4px;
  margin-bottom: -4px;
`;

const StyledThreadHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 32px;
`;
